import { useState, useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTiktok, 
  faInstagram, 
  faWhatsapp, 
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';
import { 
  // ... deine anderen Icons (Check, Mail, etc.)
  ChevronDown 
} from 'lucide-react';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'; // Für Outlook/Mail

// ─────────────────────────────────────────────────────────────
// SEO Tags
// ─────────────────────────────────────────────────────────────

const SEO = ({ lang }) => {
  const isDE = lang === 'de';
  const url = 'https://scaleflowai.com';

  const seo = isDE ? {
    title: 'Webentwickler Hamburg · React & Java Freelancer | ScaleFlowAI',
    desc: 'Freelance Web-Entwickler aus Hamburg. React, Java & Fullstack-Entwicklung für moderne Web-Apps und KI-Automatisierung. Jetzt unverbindlich anfragen.',
    keywords: 'Webentwickler Hamburg, React Developer, Java Developer, Fullstack Developer, Freelance Webentwickler, KI-Automatisierung, Webseite erstellen lassen',
  } : {
    title: 'Freelance Web Developer Hamburg · React & Java Expert | ScaleFlowAI',
    desc: 'Freelance React & Java developer based in Hamburg. Modern web apps, fullstack development & AI automation for ambitious businesses.',
    keywords: 'Freelance Web Developer, React Developer Germany, Java Consulting, Fullstack Developer Hamburg, AI Automation',
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{seo.title}</title>
      <meta name="description" content={seo.desc} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="author" content="ScaleFlowAI" />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="de" href={url} />
      <link rel="alternate" hrefLang="en" href={`${url}/en`} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.desc} />
      <meta property="og:image" content={`${url}/og-image.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={isDE ? 'de_DE' : 'en_US'} />
      <meta property="og:site_name" content="ScaleFlowAI" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.desc} />
      <meta name="twitter:image" content={`${url}/og-image.jpg`} />
      <meta name="theme-color" content="#080c14" />
    </Helmet>
  );
};

// ─────────────────────────────────────────────────────────────
// SECURITY HELPERS
// ─────────────────────────────────────────────────────────────
const sanitize = (s) =>
  String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#x27;").replace(/\//g, "&#x2F;");

const isSqlInjection = (v) =>
  /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|EXEC|EXECUTE|SCRIPT|FROM|WHERE)\b|--|;|\/\*|\*\/|xp_)/i.test(v);

const isXss = (v) => /(<script|javascript:|on\w+\s*=|<iframe|<object)/i.test(v);

const validateField = (name, value) => {
  if (isSqlInjection(value) || isXss(value))
    return { de: "Ungültige Zeichen erkannt.", en: "Invalid characters detected." };
  if (name === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    return { de: "Bitte geben Sie eine gültige E-Mail-Adresse ein.", en: "Please enter a valid email address." };
  if (name === "phone" && value && !/^[\d\s+\-()]{7,20}$/.test(value))
    return { de: "Bitte geben Sie eine gültige Telefonnummer ein.", en: "Please enter a valid phone number." };
  return null;
};

const rateLimiter = (() => {
  const attempts = [];
  return {
    check: () => {
      const now = Date.now();
      const recent = attempts.filter(t => now - t < 60000);
      if (recent.length >= 3) return false;
      attempts.push(now);
      return true;
    }
  };
})();

// ─────────────────────────────────────────────────────────────
// PRICING DATA  (easy to edit — change prices, features, CTAs here)
// ─────────────────────────────────────────────────────────────
const PRICING_PACKAGES = {
  de: [
    {
      id: "starter",
      name: "Starter",
      tagline: "Ihre digitale Grundlage",
      price: "€149",
      period: "/Monat",
      priceNote: "oder €1.499 einmalig",
      highlighted: false,
      description: "Perfekt für Gründer und kleine Unternehmen, die eine professionelle Web-Präsenz brauchen.",
      features: [
        "React-basierte Single-Page-App",
        "Bis zu 5 Sektionen / Seiten",
        "Responsive Design (Mobile/Desktop)",
        "Kontaktformular & Google Maps",
        "Basis-SEO (Meta Tags, Sitemap)",
        "DSGVO-konform & Cookie-Banner",
        "Hosting auf Vercel inklusive",
        "E-Mail-Support",
      ],
      cta: "Starter wählen",
    },
    {
      id: "professional",
      name: "Professional",
      tagline: "Wachstum & Sichtbarkeit",
      price: "€499",
      period: "/Monat",
      priceNote: "inkl. Support & Updates",
      highlighted: true,
      description: "Für wachsende Unternehmen, die mehr Leads, bessere Rankings und Marketing-Automation wollen.",
      features: [
        "Alles aus Starter, plus:",
        "Erweiterte SEO-Optimierung",
        "Lead-Generation-Funnel",
        "Blog / CMS-Integration",
        "Google Analytics & Tag Manager",
        "Newsletter-Integration",
        "A/B-Testing-Setup",
        "WhatsApp & ChatBot-Integration",
        "Monatlicher Performance-Report",
      ],
      cta: "Professional starten",
    },
    {
      id: "enterprise",
      name: "Enterprise",
      tagline: "Individuelle Enterprise-Lösungen",
      price: "Auf Anfrage",
      period: "",
      priceNote: "Maßgeschneiderte Angebote",
      highlighted: false,
      description: "Komplette KI-Automatisierung, individuelle Software und dedizierter Support für Ihr Team.",
      features: [
        "Alles aus Professional, plus:",
        "Individuelle Softwareentwicklung",
        "KI-Agenten & LLM-Pipelines",
        "Prozessautomatisierung (n8n, RPA)",
        "SaaS- / Multi-Tenant-Architektur",
        "Java / Angular Enterprise-Stack",
        "Dedicated Account Manager",
        "24/7 Premium-Support",
        "On-Premise oder Cloud-Deployment",
      ],
      cta: "Angebot anfragen",
    },
  ],
  en: [
    {
      id: "starter",
      name: "Starter",
      tagline: "Your digital foundation",
      price: "€149",
      period: "/month",
      priceNote: "or €1,499 one-time",
      highlighted: false,
      description: "Perfect for founders and small businesses needing a professional web presence.",
      features: [
        "React-based single-page app",
        "Up to 5 sections / pages",
        "Responsive design",
        "Contact form & Google Maps",
        "Basic SEO (meta, sitemap)",
        "GDPR-compliant & cookie banner",
        "Hosting on Vercel included",
        "Email support",
      ],
      cta: "Choose Starter",
    },
    {
      id: "professional",
      name: "Professional",
      tagline: "Growth & visibility",
      price: "€499",
      period: "/month",
      priceNote: "incl. support & updates",
      highlighted: true,
      description: "For growing businesses wanting more leads, better rankings and marketing automation.",
      features: [
        "Everything in Starter, plus:",
        "Advanced SEO optimisation",
        "Lead generation funnel",
        "Blog / CMS integration",
        "Google Analytics & Tag Manager",
        "Newsletter integration",
        "A/B testing setup",
        "WhatsApp & chatbot integration",
        "Monthly performance report",
      ],
      cta: "Start Professional",
    },
    {
      id: "enterprise",
      name: "Enterprise",
      tagline: "Custom enterprise solutions",
      price: "On request",
      period: "",
      priceNote: "Tailor-made offers",
      highlighted: false,
      description: "Complete AI automation, custom software and dedicated support for your team.",
      features: [
        "Everything in Professional, plus:",
        "Custom software development",
        "AI agents & LLM pipelines",
        "Process automation (n8n, RPA)",
        "SaaS / multi-tenant architecture",
        "Java / Angular enterprise stack",
        "Dedicated account manager",
        "24/7 premium support",
        "On-premise or cloud deployment",
      ],
      cta: "Request quote",
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// FAQ DATA  (easy to edit — add/remove Q&A here)
// ─────────────────────────────────────────────────────────────
const FAQ_ITEMS = {
  de: [
    { q: "Bieten Sie Support an?", a: "Ja – wir bieten laufenden Support, Wartung und kleinere Anpassungen. Je nach Paket ist dies bereits inkludiert oder kann separat als Support-Vertrag gebucht werden." },
    { q: "Welche Leistungen bieten Sie an?", a: "Wir sind Spezialisten für Web-Entwicklung (React, Next.js), KI-Automatisierung (LLMs, Agenten), individuelle Softwareentwicklung, SaaS-Plattformen sowie Java/Angular Enterprise-Projekte." },
    { q: "Wie lange dauert die Entwicklung einer Website?", a: "Eine Starter-Website ist in der Regel in 2–4 Wochen live. Größere Projekte mit KI-Integration oder Custom-Backends dauern typischerweise 6–12 Wochen." },
    { q: "Wie viel kostet eine Webseite oder KI-Automatisierung?", a: "Das hängt stark vom Umfang ab. Unsere Starter-Pakete beginnen bei €149/Monat, individuelle Enterprise-Projekte werden nach einem kostenlosen Discovery-Call individuell kalkuliert." },
    { q: "Wie optimiere ich Geschäftsprozesse mit KI?", a: "Wir analysieren Ihre repetitiven Workflows und automatisieren diese mit LLM-Pipelines, intelligenten Agenten und Tools wie n8n oder RPA. Typische Einsparung: 60–80 % der manuellen Arbeitszeit." },
    { q: "Wie generiere ich mit KI mehr Leads und Aufträge?", a: "Durch KI-gestütztes Marketing: Lead-Scoring, automatisierte Content-Erstellung, Outreach-Personalisierung und intelligente CRM-Integrationen. So erreichen Sie die richtigen Kunden zur richtigen Zeit." },
    { q: "Bieten Sie Schulungen an?", a: "Ja – wir bieten KI-Empowerment-Workshops für Teams. Ihre Mitarbeiter lernen, KI-Tools sicher und produktiv im Arbeitsalltag einzusetzen – von ChatGPT bis zu individuellen Agenten." },
    { q: "Social Media Push mit KI – wie funktioniert das?", a: "Wir setzen automatisierte Content-Pipelines auf: KI erstellt Posts, Bilder und Captions, plant die Veröffentlichung und analysiert Performance. Ideal für konstante Sichtbarkeit ohne manuellen Aufwand." },
    { q: "Bieten Sie internationalen Support?", a: "Ja – wir arbeiten weltweit. Unsere Apps unterstützen Mehrsprachigkeit von Grund auf, Meetings halten wir in Deutsch oder Englisch." },
    { q: "Sind die Websites DSGVO-konform?", a: "Alle unsere Websites sind zu 100 % DSGVO-konform gebaut: Cookie-Banner mit granularem Consent, verschlüsselte Datenübertragung, Auftragsverarbeitungsverträge und transparente Datenschutzerklärung." },
    { q: "Was ist SaaS?", a: "Software as a Service – cloud-basierte Software, die Sie per Abo nutzen, ohne sie zu installieren. Beispiele: Slack, Notion, Salesforce. Wir entwickeln solche Plattformen für Ihr Unternehmen." },
    { q: "Was bringt mir KI im Unternehmen?", a: "Drei messbare Vorteile: Effizienzsteigerung (schneller arbeiten), Kostensenkung (weniger manuelle Arbeit) und Skalierung (mehr leisten ohne mehr Personal). Der ROI liegt bei uns durchschnittlich bei 5×." },
  ],
  en: [
    { q: "Do you offer support?", a: "Yes — we offer ongoing support, maintenance and minor adjustments. Depending on your package this is included or can be booked as a separate support contract." },
    { q: "What services do you offer?", a: "We specialise in web development (React, Next.js), AI automation (LLMs, agents), custom software development, SaaS platforms and Java/Angular enterprise projects." },
    { q: "How long does it take to build a website?", a: "A Starter website typically goes live in 2–4 weeks. Larger projects with AI integration or custom backends usually take 6–12 weeks." },
    { q: "How much does a website or AI automation cost?", a: "It depends heavily on scope. Our Starter packages begin at €149/month; individual enterprise projects are quoted after a free discovery call." },
    { q: "How do I optimise business processes with AI?", a: "We analyse your repetitive workflows and automate them with LLM pipelines, intelligent agents and tools like n8n or RPA. Typical saving: 60–80 % of manual time." },
    { q: "How do I generate more leads with AI?", a: "Through AI-driven marketing: lead scoring, automated content creation, personalised outreach and intelligent CRM integrations. Reach the right customers at the right time." },
    { q: "Do you offer training?", a: "Yes — we run AI empowerment workshops for teams. Your staff learn to use AI tools safely and productively in daily work — from ChatGPT to custom agents." },
    { q: "Social media push with AI — how does it work?", a: "We set up automated content pipelines: AI creates posts, images and captions, schedules publishing and analyses performance. Ideal for consistent visibility without manual effort." },
    { q: "Do you offer international support?", a: "Yes — we work worldwide. Our apps support multilingual content natively and meetings are held in German or English." },
    { q: "Are your websites GDPR compliant?", a: "All our websites are 100 % GDPR compliant: cookie banner with granular consent, encrypted data transmission, data processing agreements and transparent privacy policy." },
    { q: "What is SaaS?", a: "Software as a Service — cloud-based software you use on subscription without installing. Examples: Slack, Notion, Salesforce. We build such platforms for your business." },
    { q: "What does AI bring my company?", a: "Three measurable benefits: efficiency (work faster), cost reduction (less manual work) and scalability (achieve more without more staff). Our average ROI is 5×." },
  ],
};

// ─────────────────────────────────────────────────────────────
// SOCIAL LINKS  (easy to edit — replace # with real URLs)
// ─────────────────────────────────────────────────────────────
const SOCIAL_LINKS = [
  { name: "TikTok", url: "https://www.tiktok.com/@scaleflowai.dev", icon: faTiktok },
  { name: "Instagram", url: "https://www.instagram.com/scaleflowai.dev/", icon: faInstagram },
  { name: "WhatsApp", url: "https://wa.me/491751656050", icon: faWhatsapp },
  { name: "LinkedIn", url: "https://www.linkedin.com/in/scaleflowai-dev-a39549405/", icon: faLinkedin },
  { name: "Email", url: "mailto:murat.brm.it@gmail.com", icon: faEnvelope },
];

// ─────────────────────────────────────────────────────────────
// TRANSLATIONS
// ─────────────────────────────────────────────────────────────
const LANG = {
  de: {
    nav: ["Startseite", "Leistungen", "Preise", "Prozess", "Über uns", "Referenzen", "FAQ", "Kontakt"],
    heroLabel: "KI & Digitalagentur",
    heroH1a: "Wir bauen das",
    heroH1b: "Web von morgen.",
    heroSub: "ScaleFlowAI entwickelt leistungsstarke Web-Apps, intelligente KI-Automatisierung und optimierte Geschäftsprozesse – damit Ihr Unternehmen ohne Grenzen wächst.",
    heroCta: "Projekt starten →",
    heroCtaAlt: "Unsere Leistungen",
    stat1: "Projekte abgeschlossen",
    stat2: "Kundenzufriedenheit",
    stat3: "Ø ROI bei Automatisierung",
    servLabel: "Was wir tun",
    servH2: "Leistungen, die wirklich bewegen",
    servSub: "Vom pixelgenauen Interface bis zum autonomen KI-Agenten – wir beherrschen den vollständigen Stack.",
    services: [
      { icon: "◈", title: "Web-Entwicklung", desc: "Blitzschnelle, barrierefreie Web-Applikationen mit modernem Stack – React, Next.js, TypeScript.", tags: ["React", "Next.js", "TypeScript", "Node.js"] },
      { icon: "⬡", title: "KI-Automatisierung", desc: "Individuelle LLM-Pipelines, intelligente Agenten und KI-Features, die Ihrem Team tausende Stunden sparen.", tags: ["LLM", "Agenten", "RAG", "OpenAI"] },
      { icon: "◉", title: "Prozessautomatisierung", desc: "End-to-End Geschäftsprozessautomatisierung – Integrationen, Workflows, RPA – damit Ihr Team sich auf das Wesentliche konzentriert.", tags: ["n8n", "Zapier", "APIs", "RPA"] },
      { icon: "◎", title: "Individuelle Softwareentwicklung", desc: "Maßgeschneiderte Softwarelösungen, exakt zugeschnitten auf Ihre Geschäftsanforderungen und Workflows.", tags: ["Custom", "DDD", "Clean Code", "Testing"] },
      { icon: "✦", title: "SaaS-Entwicklung", desc: "Skalierbare Cloud-Plattformen mit Multi-Tenant-Architektur, Subscriptions und modernen Payment-Integrationen.", tags: ["Multi-Tenant", "Stripe", "Cloud", "Scaling"] },
      { icon: "▣", title: "Enterprise-Software", desc: "Robuste, hochverfügbare Lösungen für Großunternehmen – inkl. Security, Compliance und Performance-Audits.", tags: ["Spring Boot", "Microservices", "K8s", "Security"] },
      { icon: "⬢", title: "Java & Angular Techstack", desc: "Expertise in leistungsstarken Java-Backends und modernen Angular-Frontends – aus einer Hand.", tags: ["Java", "Spring", "Angular", "RxJS"] },
      { icon: "◎", title: "Consulting", desc: "Strategische Technologieberatung zur Architektur skalierbarer Lösungen, System-Audits und digitaler Transformation.", tags: ["Strategie", "Architektur", "Audit", "Roadmap"] },
    ],
    procLabel: "Wie wir arbeiten",
    procH2: "Ein Prozess für Ergebnisse",
    process: [
      { num: "01", title: "Discovery", desc: "Tiefes Eintauchen in Ihre Ziele, Nutzer und Anforderungen." },
      { num: "02", title: "Design", desc: "Wireframes, Prototypen und ein klarer technischer Bauplan." },
      { num: "03", title: "Entwicklung", desc: "Agile Sprints, sauberer Code, wöchentliche Demos." },
      { num: "04", title: "Launch", desc: "CI/CD-Deployment, Monitoring und Go-Live-Support." },
      { num: "05", title: "Wachstum", desc: "Analytics, Iteration und kontinuierliche Optimierung." },
    ],
    aboutLabel: "Über ScaleFlowAI",
    aboutH2a: "Technologie,",
    aboutH2b: "die für Sie arbeitet",
    aboutP1: "ScaleFlowAI wurde mit einem einfachen Glauben gegründet: Jedes Unternehmen verdient Enterprise-Grade-Technologie. Wir kombinieren tiefes Engineering-Know-how mit echter Leidenschaft für Automatisierung und KI.",
    aboutP2: "Wir arbeiten eng mit Gründern, Produktteams und Enterprise-Kunden in ganz Europa zusammen – pünktlich, im Budget und stets mit höchster Qualität.",
    aboutGrid: [["Web-Apps", "Modern, schnell, barrierefrei"], ["KI-Agenten", "Intelligente Automatisierung"], ["Integrationen", "Alles verbinden"], ["Consulting", "Strategische Beratung"]],
    ctaLabel: "Kontakt",
    ctaH2a: "Lassen Sie uns gemeinsam",
    ctaH2b: "Großes schaffen",
    ctaSub: "Schreiben Sie uns – wir melden uns innerhalb von 24 Stunden. Kein Spam, kein Smalltalk – nur ein fokussiertes Gespräch über Ihr Projekt.",
    fName: "Name", fEmail: "E-Mail", fPhone: "Telefon", fService: "Leistung", fMsg: "Nachricht",
    fNamePh: "Max Mustermann", fEmailPh: "max@unternehmen.de", fPhonePh: "+49 170 …",
    fServiceOpts: ["Leistung auswählen…", "Web-Entwicklung", "KI-Automatisierung", "Prozessautomatisierung", "Consulting", "Discovery Call"],
    fMsgPh: "Erzählen Sie uns von Ihrem Projekt…",
    fSend: "Nachricht senden →",
    fSending: "Wird gesendet…",
    fWa: "WhatsApp",
    fPrivacy: "Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu. Wir geben Ihre Daten niemals weiter.",
    fRequired: "Pflichtfeld",
    fSuccess: "Nachricht gesendet!",
    fSuccessSub: "Wir melden uns innerhalb von 24 Stunden.",
    fErrRequired: "Dieses Feld ist erforderlich.",
    fErrRate: "Zu viele Anfragen. Bitte warten Sie eine Minute.",
    fConsentText: "Ich stimme zu, dass meine Angaben zur Kontaktaufnahme und für Rückfragen dauerhaft gespeichert werden.",
    fConsentLink: "Datenschutz",
    fConsentRequired: "Bitte stimmen Sie der Speicherung Ihrer Daten zu.",
    priceLabel: "Pakete",
    priceH2a: "Transparente",
    priceH2b: "Preise",
    priceSub: "Wählen Sie das Paket, das zu Ihrem Unternehmen passt. Keine versteckten Kosten.",
    priceMostPopular: "Beliebteste Wahl",
    priceIncluded: "Enthalten:",
    faqLabel: "FAQ",
    faqH2a: "Häufig gestellte",
    faqH2b: "Fragen",
    faqSub: "Alles, was Sie vor einer Zusammenarbeit wissen sollten.",
    faqStillQuestions: "Noch Fragen?",
    faqContactUs: "Kontaktieren Sie uns →",
    footerTagline: "Web-Apps · KI-Automatisierung · Prozessautomatisierung",
    footerLinks: ["Datenschutz", "Impressum", "AGB"],
    footerRights: "Alle Rechte vorbehalten.",
    footerFollow: "Folgen Sie uns",
    cookieTitle: "🍪 Cookie-Einstellungen",
    cookieText: "Wir verwenden Cookies, um Ihre Erfahrung zu verbessern. Sie können Ihre Einstellungen jederzeit anpassen.",
    cookieNec: "Notwendig",
    cookieNecDesc: "Erforderlich für die Grundfunktionen der Website",
    cookieAna: "Analyse",
    cookieAnaDesc: "Helfen uns zu verstehen, wie Besucher die Website nutzen",
    cookieMkt: "Marketing",
    cookieMktDesc: "Für personalisierte Inhalte und Anzeigen",
    cookieAcceptAll: "Alle akzeptieren",
    cookieAcceptSel: "Auswahl speichern",
    cookieDecline: "Ablehnen",
    botWelcome: "👋 Hallo! Ich bin der ScaleFlowAI-Assistent. Wie kann ich Ihnen helfen?",
    botOptions: ["Web-Entwicklung", "KI-Automatisierung", "Prozessautomatisierung", "Preise", "Kontakt aufnehmen"],
    botInputPh: "Nachricht eingeben…",
    botSend: "Senden",
    botOnline: "Online",
    botWaHint: "Oder direkt auf",
    getStarted: "Loslegen",
    langSwitch: "EN",
    refLabel: "Referenzen",
    refH2a: "Was unsere",
    refH2b: "Kunden sagen",
    refSub: "Echte Projekte, echte Ergebnisse – das sagen Unternehmen, die mit uns gebaut haben.",
    privacyTitle: "Datenschutzerklärung",
    privacyBody: `ScaleFlowAI nimmt den Schutz Ihrer persönlichen Daten ernst.

**Verantwortlicher:** ScaleFlowAI, murat.brm.it@gmail.com

**Erhobene Daten:** Beim Kontaktformular erheben wir Name, E-Mail-Adresse, Telefonnummer (optional) und Ihre Nachricht ausschließlich zur Bearbeitung Ihrer Anfrage.

**Weitergabe:** Ihre Daten werden nicht an Dritte weitergegeben. Die Übermittlung erfolgt verschlüsselt (TLS) über Formspree.io.

**Cookies:** Wir verwenden ausschließlich technisch notwendige Cookies sowie optionale Analyse-Cookies (nur mit Ihrer Zustimmung).

**Ihre Rechte:** Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung Ihrer Daten. Kontakt: murat.brm.it@gmail.com

**Hosting:** Die Website wird auf Vercel gehostet (Vercel Inc., USA). Es gilt die DSGVO-konforme Datenverarbeitungsvereinbarung von Vercel.`,
    impressumTitle: "Impressum",
    impressumBody: `Angaben gemäß § 5 TMG

**ScaleFlowAI**
E-Mail: murat.brm.it@gmail.com

Kurier Füchse UG (haftungsbeschränkt)\n
Hermann-Wöhlke-Weg 7
21039 Börnsen

**Sitz und Registergericht**

**Handelsregister beim Amtsgericht Hamburg, Nr. HRB 190425**

**Vertreten durch:** Murat Bayram

**Telefon**: +49/175-1656-050

**Haftung für Inhalte:** Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir keine Gewähr übernehmen.

**Streitschlichtung:** Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr`,
    agbTitle: "Allgemeine Geschäftsbedingungen",
    agbBody: `**§ 1 Geltungsbereich**
Diese AGB gelten für alle Verträge zwischen ScaleFlowAI und seinen Kunden über Web-Entwicklung, KI-Automatisierung, Prozessautomatisierung und Consulting-Leistungen.

**§ 2 Vertragsschluss**
Angebote von ScaleFlowAI sind freibleibend. Ein Vertrag kommt durch schriftliche Auftragsbestätigung zustande.

**§ 3 Leistungsumfang**
Der genaue Leistungsumfang wird im jeweiligen Projektvertrag oder Statement of Work (SOW) festgelegt.

**§ 4 Zahlungsbedingungen**
Rechnungen sind innerhalb von 14 Tagen nach Rechnungsstellung ohne Abzug zahlbar, sofern nicht anders vereinbart.

**§ 5 Urheberrecht**
Alle erstellten Werke bleiben bis zur vollständigen Bezahlung Eigentum von ScaleFlowAI.

**§ 6 Haftungsbeschränkung**
ScaleFlowAI haftet nur für Schäden, die auf vorsätzlichem oder grob fahrlässigem Verhalten beruhen.

**§ 7 Gerichtsstand**
Es gilt deutsches Recht. Gerichtsstand ist der Sitz von ScaleFlowAI.`,
    modalClose: "Schließen",
  },
  en: {
    nav: ["Home", "Services", "Pricing", "Process", "About", "References", "FAQ", "Contact"],
    heroLabel: "AI & Digital Agency",
    heroH1a: "We build",
    heroH1b: "tomorrow's web.",
    heroSub: "ScaleFlowAI crafts high-performance web apps, intelligent AI automation and streamlined business processes — so your company scales without limits.",
    heroCta: "Start a project →",
    heroCtaAlt: "Our services",
    stat1: "Projects delivered",
    stat2: "Client satisfaction",
    stat3: "Avg. ROI on automation",
    servLabel: "What we do",
    servH2: "Services that move the needle",
    servSub: "From pixel-perfect interfaces to autonomous AI agents — we cover the full stack.",
    services: [
      { icon: "◈", title: "Web Development", desc: "Blazing-fast, accessible web applications built with modern stacks — React, Next.js, TypeScript.", tags: ["React", "Next.js", "TypeScript", "Node.js"] },
      { icon: "⬡", title: "AI Automation", desc: "Custom LLM pipelines, intelligent agents and AI-powered features that save your team thousands of hours.", tags: ["LLM", "Agents", "RAG", "OpenAI"] },
      { icon: "◉", title: "Process Automation", desc: "End-to-end business process automation — integrations, workflows, RPA — so your team focuses on what matters.", tags: ["n8n", "Zapier", "APIs", "RPA"] },
      { icon: "◎", title: "Custom Software Development", desc: "Tailor-made software solutions, precisely aligned with your business needs and workflows.", tags: ["Custom", "DDD", "Clean Code", "Testing"] },
      { icon: "✦", title: "SaaS Development", desc: "Scalable cloud platforms with multi-tenant architecture, subscriptions and modern payment integrations.", tags: ["Multi-Tenant", "Stripe", "Cloud", "Scaling"] },
      { icon: "▣", title: "Enterprise Software", desc: "Robust, high-availability solutions for large organisations — including security, compliance and performance audits.", tags: ["Spring Boot", "Microservices", "K8s", "Security"] },
      { icon: "⬢", title: "Java & Angular Techstack", desc: "Expertise in high-performance Java backends and modern Angular frontends — from a single source.", tags: ["Java", "Spring", "Angular", "RxJS"] },
      { icon: "◎", title: "Consulting", desc: "Strategic tech consulting to architect scalable solutions, audit existing systems and roadmap your digital transformation.", tags: ["Strategy", "Architecture", "Audit", "Roadmap"] },
    ],
    procLabel: "How we work",
    procH2: "A process built for results",
    process: [
      { num: "01", title: "Discovery", desc: "Deep-dive into your goals, users and constraints." },
      { num: "02", title: "Design", desc: "Wireframes, prototypes and a clear technical blueprint." },
      { num: "03", title: "Build", desc: "Agile sprints, clean code, weekly demos." },
      { num: "04", title: "Launch", desc: "CI/CD deployment, monitoring and go-live support." },
      { num: "05", title: "Grow", desc: "Analytics, iteration and ongoing optimisation." },
    ],
    aboutLabel: "About ScaleFlowAI",
    aboutH2a: "Technology that",
    aboutH2b: "works for you",
    aboutP1: "ScaleFlowAI was founded with a simple belief: every business deserves enterprise-grade technology. We combine deep engineering expertise with a genuine passion for automation and AI.",
    aboutP2: "We work closely with founders, product teams and enterprise clients across Europe — delivering on time, on budget, and always with exceptional quality.",
    aboutGrid: [["Web Apps", "Modern, fast, accessible"], ["AI Agents", "Intelligent automation"], ["Integrations", "Connect everything"], ["Consulting", "Strategic guidance"]],
    ctaLabel: "Contact",
    ctaH2a: "Let's build something",
    ctaH2b: "great together",
    ctaSub: "Drop us a message and we'll get back to you within 24 hours. No spam, no fluff — just a focused conversation about your project.",
    fName: "Name", fEmail: "Email", fPhone: "Phone", fService: "Service", fMsg: "Message",
    fNamePh: "Jane Smith", fEmailPh: "jane@company.com", fPhonePh: "+49 170 …",
    fServiceOpts: ["Select a service…", "Web Development", "AI Automation", "Process Automation", "Consulting", "Discovery Call"],
    fMsgPh: "Tell us about your project…",
    fSend: "Send message →",
    fSending: "Sending…",
    fWa: "WhatsApp",
    fPrivacy: "By submitting you agree to our privacy policy. We never share your data.",
    fRequired: "Required",
    fSuccess: "Message sent!",
    fSuccessSub: "We'll be in touch within 24 hours.",
    fErrRequired: "This field is required.",
    fErrRate: "Too many requests. Please wait a minute.",
    fConsentText: "I consent to having my data permanently stored for contact purposes and follow-up enquiries.",
    fConsentLink: "Privacy Policy",
    fConsentRequired: "Please consent to the storage of your data.",
    priceLabel: "Packages",
    priceH2a: "Transparent",
    priceH2b: "pricing",
    priceSub: "Choose the package that fits your business. No hidden costs.",
    priceMostPopular: "Most popular",
    priceIncluded: "Included:",
    faqLabel: "FAQ",
    faqH2a: "Frequently asked",
    faqH2b: "questions",
    faqSub: "Everything you should know before we work together.",
    faqStillQuestions: "Still have questions?",
    faqContactUs: "Get in touch →",
    footerTagline: "Web Apps · AI Automation · Process Automation",
    footerLinks: ["Privacy", "Imprint", "Terms"],
    footerRights: "All rights reserved.",
    footerFollow: "Follow us",
    cookieTitle: "🍪 Cookie Settings",
    cookieText: "We use cookies to improve your experience. You can adjust your settings at any time.",
    cookieNec: "Necessary",
    cookieNecDesc: "Required for basic website functionality",
    cookieAna: "Analytics",
    cookieAnaDesc: "Help us understand how visitors use the website",
    cookieMkt: "Marketing",
    cookieMktDesc: "For personalised content and ads",
    cookieAcceptAll: "Accept all",
    cookieAcceptSel: "Save selection",
    cookieDecline: "Decline",
    botWelcome: "👋 Hi! I'm the ScaleFlowAI assistant. How can I help you today?",
    botOptions: ["Web Development", "AI Automation", "Process Automation", "Pricing", "Contact us"],
    botInputPh: "Type a message…",
    botSend: "Send",
    botOnline: "Online",
    botWaHint: "Or chat on",
    getStarted: "Get Started",
    langSwitch: "DE",
    refLabel: "References",
    refH2a: "What our",
    refH2b: "clients say",
    refSub: "Real projects, real results — here's what companies who built with us have to say.",
    privacyTitle: "Privacy Policy",
    privacyBody: `ScaleFlowAI takes the protection of your personal data seriously.

**Controller:** ScaleFlowAI, murat.brm.it@gmail.com

**Data collected:** Via the contact form we collect your name, email address, phone number (optional) and your message solely to process your enquiry.

**Disclosure:** Your data is not shared with third parties. Transmission is encrypted (TLS) via Formspree.io.

**Cookies:** We use only technically necessary cookies and optional analytics cookies (only with your consent).

**Your rights:** You have the right to access, rectify, erase and restrict the processing of your data. Contact: murat.brm.it@gmail.com

**Hosting:** This website is hosted on Vercel (Vercel Inc., USA) under a GDPR-compliant data processing agreement.`,
    impressumTitle: "Legal Notice",
    impressumBody: `Information pursuant to § 5 TMG

**ScaleFlowAI**
Email: murat.brm.it@gmail.com

**Note:** Please enter your full address, VAT ID and trade register number (if applicable) before going live.

**Liability for content:** The contents of our pages have been created with the utmost care.

**Dispute resolution:** The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr`,
    agbTitle: "Terms & Conditions",
    agbBody: `**§ 1 Scope**
These T&Cs apply to all contracts between ScaleFlowAI and its clients.

**§ 2 Formation of contract**
Offers from ScaleFlowAI are non-binding. A contract is formed upon written order confirmation.

**§ 3 Scope of services**
The exact scope is defined in the respective project contract or SOW.

**§ 4 Payment terms**
Invoices are due within 14 days of the invoice date.

**§ 5 Copyright**
All created works remain the property of ScaleFlowAI until full payment is received.

**§ 6 Limitation of liability**
ScaleFlowAI is only liable for damages caused by intentional or grossly negligent conduct.

**§ 7 Jurisdiction**
German law applies.`,
    modalClose: "Close",
  }
};

const WA_NUMBER = "491751656050";
const waLink = (msg = "") => `https://wa.me/${WA_NUMBER}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;

const BOT_FLOWS = {
  de: {
    "Web-Entwicklung": { text: "Wir entwickeln moderne Web-Apps. Sollen wir das direkt besprechen?", options: ["Mehr erfahren", "Zum Kontaktformular"] },
    "KI-Automatisierung": { text: "Wir spezialisieren uns auf LLM-Pipelines und KI-Agenten. Möchten Sie unser Team kontaktieren?", options: ["Ja, verbinden", "Mehr erfahren"] },
    "Prozessautomatisierung": { text: "Wir automatisieren repetitive Workflows. Möchten Sie ein kostenloses Audit?", options: ["Ja, kostenloses Audit", "Mehr erfahren"] },
    "Preise": { text: "Die Preise hängen vom Umfang ab. Am besten ein kurzes kostenloses Discovery-Call!", options: ["Call buchen", "Zum Kontaktformular"] },
    "Kontakt aufnehmen": { text: "Sehr gerne! Füllen Sie einfach das Kontaktformular aus.", options: ["Zum Kontaktformular"] },
    "Mehr erfahren": { text: "Wir helfen Ihnen gerne mit konkreten Beispielen. Schreiben Sie uns einfach!", options: ["Zum Kontaktformular"] },
    "Ja, verbinden": { text: "Super! Füllen Sie das Kontaktformular aus und wir melden uns.", options: ["Zum Kontaktformular"] },
    "Ja, kostenloses Audit": { text: "Perfekt – bitte im Formular 'kostenloses Audit' erwähnen.", options: ["Zum Kontaktformular"] },
    "Call buchen": { text: "Bitte im Kontaktformular 'Discovery Call' auswählen.", options: ["Zum Kontaktformular"] },
  },
  en: {
    "Web Development": { text: "We build modern web apps. Want to discuss your project?", options: ["Tell me more", "Go to Contact Form"] },
    "AI Automation": { text: "We specialise in LLM pipelines and AI agents. Shall I connect you?", options: ["Yes, connect me", "Tell me more"] },
    "Process Automation": { text: "We automate repetitive workflows. Want a free audit?", options: ["Yes, free audit", "Tell me more"] },
    "Pricing": { text: "Pricing depends on scope. Best next step: a free discovery call!", options: ["Book a call", "Go to Contact Form"] },
    "Contact us": { text: "Sure! Fill in the contact form and we'll get back to you.", options: ["Go to Contact Form"] },
    "Tell me more": { text: "We'd love to walk you through case studies. Drop us a message!", options: ["Go to Contact Form"] },
    "Yes, connect me": { text: "Awesome! Fill in the contact form and we'll get back to you.", options: ["Go to Contact Form"] },
    "Yes, free audit": { text: "Perfect — mention 'free audit' in the form.", options: ["Go to Contact Form"] },
    "Book a call": { text: "Please select 'Discovery Call' in the contact form.", options: ["Go to Contact Form"] },
  }
};

// ─────────────────────────────────────────────────────────────
// THEME
// ─────────────────────────────────────────────────────────────
const T = {
  bg: "#080c14",
  surface: "#0d1220",
  surfaceHi: "#111827",
  border: "#1f2a3c",
  accent: "#7c3aed",
  accentLt: "#a78bfa",
  accentGlow: "#7c3aed55",
  teal: "#06b6d4",
  text: "#e2e8f0",
  muted: "#64748b",
  white: "#ffffff",
};

// Section IDs  (order MUST match nav arrays above)
// Home(0), Services(1), Pricing(2), Process(3), About(4), References(5), FAQ(6), Contact(7)
const SECTION_IDS = ["sec-home", "sec-services", "sec-pricing", "sec-process", "sec-about", "sec-references", "sec-faq", "sec-contact"];
const CONTACT_IDX = 7;
const scrollTo = (idx) => document.getElementById(SECTION_IDS[idx])?.scrollIntoView({ behavior: "smooth" });

// ─────────────────────────────────────────────────────────────
// GLOBAL CSS
// ─────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Bricolage+Grotesque:wght@400;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{background:${T.bg};color:${T.text};font-family:'Plus Jakarta Sans',sans-serif;overflow-x:hidden;line-height:1.6}
::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:${T.bg}}::-webkit-scrollbar-thumb{background:${T.accent};border-radius:2px}
::selection{background:${T.accent};color:#fff}
a{color:inherit;text-decoration:none}
button{cursor:pointer;border:none;background:none;font-family:inherit}
input,textarea,select{font-family:inherit}
.hf{font-family:'Bricolage Grotesque',sans-serif}

body::after{content:'';position:fixed;inset:0;pointer-events:none;z-index:9999;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
  opacity:0.022;background-size:160px}

@keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes pulseRing{0%,100%{transform:scale(1);opacity:.7}50%{transform:scale(1.22);opacity:.2}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes gradShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}

.fu{animation:fadeUp .55s ease both}
.fu1{animation-delay:.08s}.fu2{animation-delay:.18s}.fu3{animation-delay:.3s}.fu4{animation-delay:.42s}

.dotgrid{background-image:radial-gradient(${T.border} 1px,transparent 1px);background-size:28px 28px}

.gcard{position:relative;transition:all .25s ease}
.gcard::before{content:'';position:absolute;inset:0;border-radius:12px;padding:1px;
  background:linear-gradient(135deg,${T.accent}44,${T.teal}22,transparent 60%);
  -webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);
  -webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none;opacity:0;transition:opacity .3s}
.gcard:hover::before{opacity:1}

.desktop-nav{display:flex}
.hamburger-btn{display:none}
@media(max-width:900px){
  .desktop-nav{display:none!important}
  .hamburger-btn{display:flex!important}
}

.modal-body p{margin-bottom:10px;font-size:13px;color:${T.muted};line-height:1.75}
.modal-body strong{color:${T.text};font-weight:600}

/* FAQ accordion animation */
.faq-answer{overflow:hidden;transition:max-height .35s ease, opacity .3s ease, margin-top .3s ease}
`;

// ─────────────────────────────────────────────────────────────
// SMALL COMPONENTS
// ─────────────────────────────────────────────────────────────
const SLabel = ({ children }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
    <span style={{ width: 20, height: 2, background: `linear-gradient(90deg,${T.accent},${T.teal})`, display: "block", borderRadius: 2 }} />
    <span style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: T.accentLt }}>
      {children}
    </span>
  </div>
);

const Btn = ({ children, onClick, variant = "primary", as: As = "button", href, type = "button", disabled = false, style: sx = {} }) => {
  const [h, setH] = useState(false);
  const base = {
    display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 26px", borderRadius: 8,
    fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 13,
    letterSpacing: "0.05em", textTransform: "uppercase", transition: "all .2s ease",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1, ...sx
  };
  const s = variant === "primary"
    ? { ...base, background: h && !disabled ? T.accentLt : T.accent, color: "#fff", boxShadow: h && !disabled ? `0 0 32px ${T.accentGlow}` : `0 0 16px ${T.accentGlow}44` }
    : { ...base, background: "transparent", color: T.accentLt, border: `1px solid ${T.accent}55`, boxShadow: h && !disabled ? `0 0 16px ${T.accentGlow}` : "none" };
  if (As === "a") return <a href={href} target="_blank" rel="noopener noreferrer" style={s} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>{children}</a>;
  return <button type={type} disabled={disabled} style={s} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>{children}</button>;
};

// ─────────────────────────────────────────────────────────────
// LEGAL MODAL
// ─────────────────────────────────────────────────────────────
const LegalModal = ({ lang, which, onClose }) => {
  const t = LANG[lang];
  const map = {
    0: { title: t.privacyTitle, body: t.privacyBody },
    1: { title: t.impressumTitle, body: t.impressumBody },
    2: { title: t.agbTitle, body: t.agbBody },
  };
  const { title, body } = map[which] || map[0];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const renderBody = (text) =>
    text.split("\n\n").map((para, i) => {
      const html = para.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return <p key={i} dangerouslySetInnerHTML={{ __html: html }} style={{ marginBottom: 12, fontSize: 13, color: T.muted, lineHeight: 1.75 }} />;
    });

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "#000c", zIndex: 3000,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px", animation: "fadeIn .2s ease",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16,
        maxWidth: 640, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px #000000bb",
      }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 className="hf" style={{ fontSize: 18, fontWeight: 700, color: T.white }}>{title}</h2>
          <button onClick={onClose} aria-label={t.modalClose} style={{ color: T.muted, fontSize: 20, lineHeight: 1, padding: 4 }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
          {renderBody(body)}
        </div>
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, textAlign: "right" }}>
          <Btn onClick={onClose} sx={{ padding: "8px 20px", fontSize: 12 }}>{t.modalClose}</Btn>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// LOGO SVG
// ─────────────────────────────────────────────────────────────
const Logo = ({ size = 32 }) => (
    <img
        height={size}
        width={size}
        aria-hidden="true"
        src="/firmenlogo_neu social_media.png"
        alt="ScaleFlowAI Logo"
        style={{ height: size, width: "auto" }}
    />
);

/*const Logo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="lg1" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop stopColor={T.accent} />
        <stop offset="1" stopColor={T.teal} />
      </linearGradient>
    </defs>
    <path d="M20 2 L36 11 L36 29 L20 38 L4 29 L4 11 Z" stroke="url(#lg1)" strokeWidth="1.5" fill="none" />
    <path d="M12 20 L20 14 L28 20 L20 26 Z" fill="url(#lg1)" opacity="0.9" />
    <circle cx="20" cy="20" r="3" fill="#fff" />
  </svg>
);*/

// ─────────────────────────────────────────────────────────────
// HEADER
// ─────────────────────────────────────────────────────────────
const Header = ({ menuOpen, setMenuOpen, lang, setLang }) => {
  const [scrolled, setScrolled] = useState(false);
  const t = LANG[lang];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      padding: "0 clamp(20px,5vw,64px)", height: 64,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: scrolled ? `${T.bg}f0` : "transparent",
      backdropFilter: scrolled ? "blur(20px)" : "none",
      borderBottom: scrolled ? `1px solid ${T.border}` : "none",
      transition: "all .3s ease",
    }}>
      <button onClick={() => scrollTo(0)} aria-label="ScaleFlowAI – zur Startseite" style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none" }}>
        <Logo size={30} />
        <span className="hf" style={{ fontWeight: 800, fontSize: 18, color: T.white }}>
          Scale<span style={{ color: T.accentLt }}>Flow</span><span style={{ color: T.teal }}>AI</span>
        </span>
      </button>

      <nav className="desktop-nav" style={{ gap: 22, alignItems: "center" }} aria-label="Hauptnavigation">
        {t.nav.map((l, i) => (
          <button key={i} onClick={() => scrollTo(i)} style={{
            background: "none", border: "none", color: T.muted, fontSize: 13,
            fontFamily: "'Plus Jakarta Sans',sans-serif", cursor: "pointer", transition: "color .2s", padding: "4px 0",
          }}
            onMouseEnter={e => e.target.style.color = T.accentLt}
            onMouseLeave={e => e.target.style.color = T.muted}>
            {l}
          </button>
        ))}
        <button onClick={() => setLang(l => l === "de" ? "en" : "de")} aria-label="Sprache wechseln" style={{
          padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.border}`,
          color: T.muted, fontSize: 12, fontFamily: "'Bricolage Grotesque',sans-serif",
          fontWeight: 700, letterSpacing: "0.08em", transition: "all .2s", background: "transparent",
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accentLt; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
          {t.langSwitch}
        </button>
        <Btn onClick={() => scrollTo(CONTACT_IDX)} sx={{ padding: "7px 18px", fontSize: 12 }}>{t.getStarted}</Btn>
      </nav>

      <button
        className="hamburger-btn"
        onClick={() => setMenuOpen(p => !p)}
        aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
        aria-expanded={menuOpen}
        style={{ flexDirection: "column", gap: 5, padding: 6, background: "none", border: "none" }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            display: "block", width: 22, height: 2, background: menuOpen ? T.accent : T.text, borderRadius: 2,
            transition: "all .25s ease",
            transform: menuOpen ? (i === 0 ? "translateY(7px) rotate(45deg)" : i === 2 ? "translateY(-7px) rotate(-45deg)" : "scaleX(0)") : "none",
          }} />
        ))}
      </button>
    </header>
  );
};

// ─────────────────────────────────────────────────────────────
// SLIDE MENU
// ─────────────────────────────────────────────────────────────
const SlideMenu = ({ open, setOpen, lang, setLang }) => {
  const t = LANG[lang];
  const go = (i) => { setOpen(false); scrollTo(i); };

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setOpen]);

  return (
    <>
      {open && <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "#000b", zIndex: 1001 }} aria-hidden="true" />}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: "min(320px,92vw)",
          background: T.surface, borderLeft: `1px solid ${T.border}`,
          zIndex: 1002, padding: "72px 36px 40px",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform .34s cubic-bezier(.4,0,.2,1)",
          display: "flex", flexDirection: "column", gap: 4, overflowY: "auto",
        }}>
        <button onClick={() => setOpen(false)} aria-label="Menü schließen" style={{ position: "absolute", top: 18, right: 22, color: T.muted, fontSize: 20 }}>✕</button>
        <p style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: T.muted, marginBottom: 20 }}>
          Navigation
        </p>
        {t.nav.map((l, i) => (
          <button key={i} onClick={() => go(i)} style={{
            background: "none", border: "none", textAlign: "left",
            fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 20,
            color: T.text, cursor: "pointer", padding: "10px 0",
            borderBottom: `1px solid ${T.border}`, transition: "color .2s",
          }}
            onMouseEnter={e => e.currentTarget.style.color = T.accentLt}
            onMouseLeave={e => e.currentTarget.style.color = T.text}>
            {l}
          </button>
        ))}
        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
          <button onClick={() => setLang(l => l === "de" ? "en" : "de")} aria-label="Sprache wechseln" style={{
            padding: "8px 0", borderRadius: 6, border: `1px solid ${T.border}`,
            color: T.muted, fontSize: 13, fontFamily: "'Bricolage Grotesque',sans-serif",
            fontWeight: 700, background: "transparent", width: "100%",
          }}>
            {lang === "de" ? "🇬🇧 English" : "🇩🇪 Deutsch"}
          </button>
          <Btn onClick={() => go(CONTACT_IDX)} sx={{ width: "100%", justifyContent: "center" }}>{t.getStarted} →</Btn>
          <p style={{ fontSize: 12, color: T.muted }}>murat.brm.it@gmail.com</p>
        </div>
      </aside>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────
const Hero = ({ lang }) => {
  const t = LANG[lang];
  return (
    <section id="sec-home" style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      padding: "120px clamp(20px,5vw,64px) 80px", position: "relative", overflow: "hidden"
    }} className="dotgrid">
      <div style={{
        position: "absolute", top: "15%", right: "-5%", width: "45vw", maxWidth: 640, aspectRatio: "1",
        background: `radial-gradient(circle, ${T.accentGlow} 0%, transparent 65%)`,
        borderRadius: "50%", pointerEvents: "none", animation: "float 6s ease-in-out infinite"
      }} aria-hidden="true" />
      <div style={{
        position: "absolute", bottom: "10%", left: "5%", width: "25vw", maxWidth: 360, aspectRatio: "1",
        background: `radial-gradient(circle, ${T.teal}18 0%, transparent 65%)`,
        borderRadius: "50%", pointerEvents: "none", animation: "float 8s ease-in-out infinite 2s"
      }} aria-hidden="true" />

      <div style={{ maxWidth: 860, position: "relative", zIndex: 1 }}>
        <SLabel>{t.heroLabel}</SLabel>
        <h1 className="hf fu" style={{ fontSize: "clamp(44px,7.5vw,100px)", fontWeight: 800, lineHeight: 1.0, color: T.white, marginBottom: 28 }}>
          {t.heroH1a}<br />
          <span style={{ background: `linear-gradient(135deg,${T.accent},${T.teal})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {t.heroH1b}
          </span>
        </h1>
        <p className="fu fu1" style={{ fontSize: "clamp(15px,1.8vw,19px)", color: T.muted, maxWidth: 500, lineHeight: 1.75, marginBottom: 44 }}>
          {t.heroSub}
        </p>
        <div className="fu fu2" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <Btn onClick={() => scrollTo(CONTACT_IDX)}>{t.heroCta}</Btn>
          <Btn variant="outline" onClick={() => scrollTo(1)}>{t.heroCtaAlt}</Btn>
        </div>

        <div className="fu fu3" style={{ marginTop: 72, display: "flex", gap: "clamp(24px,4vw,64px)", flexWrap: "wrap" }}>
          {[["50+", t.stat1], ["98%", t.stat2], ["5×", t.stat3]].map(([num, label]) => (
            <div key={label}>
              <div className="hf" style={{
                fontWeight: 800, fontSize: "clamp(28px,4vw,42px)",
                background: `linear-gradient(135deg,${T.white},${T.accentLt})`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"
              }}>{num}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// SERVICES
// ─────────────────────────────────────────────────────────────
const Services = ({ lang }) => {
  const t = LANG[lang];
  const [active, setActive] = useState(null);
  return (
    <section id="sec-services" style={{ padding: "120px clamp(20px,5vw,64px)", background: T.surface }}>
      <SLabel>{t.servLabel}</SLabel>
      <h2 className="hf" style={{ fontSize: "clamp(30px,5vw,52px)", fontWeight: 800, color: T.white, marginBottom: 14, maxWidth: 560 }}>{t.servH2}</h2>
      <p style={{ color: T.muted, maxWidth: 460, marginBottom: 60, lineHeight: 1.75 }}>{t.servSub}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16 }}>
        {t.services.map((s, i) => (
          <div key={s.title} className="gcard"
            onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
            onClick={() => scrollTo(CONTACT_IDX)}
            role="button" tabIndex={0}
            onKeyDown={e => e.key === "Enter" && scrollTo(CONTACT_IDX)}
            aria-label={`${s.title} – ${s.desc}`}
            style={{
              padding: "36px 28px", borderRadius: 12, cursor: "pointer",
              background: active === i ? `${T.accent}0a` : T.bg,
              border: `1px solid ${active === i ? T.accent + "44" : T.border}`,
              transition: "all .25s ease",
            }}>
            <div style={{
              fontSize: 30, marginBottom: 18, background: `linear-gradient(135deg,${T.accent},${T.teal})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"
            }}>{s.icon}</div>
            <h3 className="hf" style={{ fontSize: 20, fontWeight: 700, color: T.white, marginBottom: 10 }}>{s.title}</h3>
            <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {s.tags.map(tag => (
                <span key={tag} style={{
                  fontSize: 10, padding: "3px 9px", border: `1px solid ${T.border}`,
                  borderRadius: 4, color: T.muted, letterSpacing: "0.06em"
                }}>{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// PRICING SECTION  (NEW)
// ─────────────────────────────────────────────────────────────
const PricingCard = ({ pkg, lang }) => {
  const [hover, setHover] = useState(false);
  const t = LANG[lang];
  const h = pkg.highlighted;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        padding: "40px 32px",
        borderRadius: 16,
        background: h ? `linear-gradient(180deg, ${T.accent}14 0%, ${T.surface} 60%)` : T.surface,
        border: h
          ? `1px solid ${T.accent}`
          : `1px solid ${hover ? T.accent + "55" : T.border}`,
        boxShadow: h
          ? `0 12px 48px ${T.accentGlow}, 0 2px 12px #00000055`
          : hover ? `0 8px 32px ${T.accentGlow}66` : "0 2px 12px #00000033",
        transition: "all .3s ease",
        display: "flex", flexDirection: "column", gap: 20,
        transform: h ? "scale(1.02)" : "scale(1)",
      }}
    >
      {h && (
        <div style={{
          position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
          background: `linear-gradient(135deg, ${T.accent}, ${T.teal})`,
          color: "#fff", padding: "4px 14px", borderRadius: 999,
          fontSize: 10, fontFamily: "'Bricolage Grotesque',sans-serif",
          fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
          boxShadow: `0 4px 16px ${T.accentGlow}`,
        }}>★ {t.priceMostPopular}</div>
      )}

      <div>
        <h3 className="hf" style={{ fontSize: 22, fontWeight: 800, color: T.white, marginBottom: 4 }}>{pkg.name}</h3>
        <p style={{ fontSize: 12, color: T.accentLt, letterSpacing: "0.05em" }}>{pkg.tagline}</p>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
        <span className="hf" style={{
          fontSize: 38, fontWeight: 800,
          background: `linear-gradient(135deg,${T.white},${T.accentLt})`,
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          lineHeight: 1,
        }}>{pkg.price}</span>
        {pkg.period && <span style={{ fontSize: 14, color: T.muted }}>{pkg.period}</span>}
      </div>
      {pkg.priceNote && <p style={{ fontSize: 11, color: T.muted, marginTop: -12 }}>{pkg.priceNote}</p>}

      <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>{pkg.description}</p>

      <div style={{ height: 1, background: `linear-gradient(90deg, ${T.accent}33, transparent)` }} />

      <div>
        <p style={{ fontSize: 10, color: T.muted, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 12, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
          {t.priceIncluded}
        </p>
        <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {pkg.features.map((f, i) => (
            <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: T.text, lineHeight: 1.5 }}>
              <span aria-hidden="true" style={{
                flexShrink: 0, marginTop: 2,
                width: 16, height: 16, borderRadius: "50%",
                background: h ? `linear-gradient(135deg,${T.accent},${T.teal})` : `${T.accent}22`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke={h ? "#fff" : T.accentLt} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ marginTop: "auto", paddingTop: 10 }}>
        <Btn
          variant={h ? "primary" : "outline"}
          onClick={() => scrollTo(CONTACT_IDX)}
          sx={{ width: "100%", justifyContent: "center" }}
        >
          {pkg.cta} →
        </Btn>
      </div>
    </div>
  );
};

const Pricing = ({ lang }) => {
  const t = LANG[lang];
  const packages = PRICING_PACKAGES[lang];

  return (
    <section id="sec-pricing" style={{
      padding: "120px clamp(20px,5vw,64px)",
      background: T.bg,
      position: "relative", overflow: "hidden",
    }} className="dotgrid">
      <div aria-hidden="true" style={{
        position: "absolute", top: "20%", left: "-10%",
        width: "40vw", maxWidth: 500, aspectRatio: "1",
        background: `radial-gradient(circle, ${T.accentGlow} 0%, transparent 65%)`,
        borderRadius: "50%", pointerEvents: "none",
      }} />

      <div style={{ position: "relative", zIndex: 1 }}>
        <SLabel>{t.priceLabel}</SLabel>
        <h2 className="hf" style={{ fontSize: "clamp(30px,5vw,52px)", fontWeight: 800, color: T.white, marginBottom: 14, lineHeight: 1.1 }}>
          {t.priceH2a}{" "}
          <span style={{
            background: `linear-gradient(135deg,${T.accent},${T.teal})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>{t.priceH2b}</span>
        </h2>
        <p style={{ color: T.muted, maxWidth: 500, marginBottom: 72, lineHeight: 1.75 }}>{t.priceSub}</p>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
          gap: 24,
          alignItems: "stretch",
          maxWidth: 1200,
        }}>
          {packages.map(pkg => <PricingCard key={pkg.id} pkg={pkg} lang={lang} />)}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// PROCESS
// ─────────────────────────────────────────────────────────────
const Process = ({ lang }) => {
  const t = LANG[lang];
  return (
    <section id="sec-process" style={{ padding: "120px clamp(20px,5vw,64px)" }} className="dotgrid">
      <SLabel>{t.procLabel}</SLabel>
      <h2 className="hf" style={{ fontSize: "clamp(30px,5vw,52px)", fontWeight: 800, color: T.white, marginBottom: 60, maxWidth: 460 }}>{t.procH2}</h2>
      <div style={{ maxWidth: 720, display: "flex", flexDirection: "column" }}>
        {t.process.map((s, i) => (
          <div key={s.num} style={{
            display: "flex", gap: 28, alignItems: "flex-start",
            padding: "24px 0", borderBottom: i < t.process.length - 1 ? `1px solid ${T.border}` : "none",
          }}>
            <span className="hf" style={{ fontWeight: 800, fontSize: 13, color: T.accent, minWidth: 30, letterSpacing: "0.08em", paddingTop: 3 }}>{s.num}</span>
            <div>
              <h3 className="hf" style={{ fontSize: 19, fontWeight: 700, color: T.white, marginBottom: 4 }}>{s.title}</h3>
              <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// ABOUT
// ─────────────────────────────────────────────────────────────
const About = ({ lang }) => {
  const t = LANG[lang];
  return (
    <section id="sec-about" style={{ padding: "120px clamp(20px,5vw,64px)", background: T.surface }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 64, alignItems: "center" }}>
        <div>
          <SLabel>{t.aboutLabel}</SLabel>
          <h2 className="hf" style={{ fontSize: "clamp(30px,5vw,48px)", fontWeight: 800, color: T.white, marginBottom: 22, lineHeight: 1.1 }}>
            {t.aboutH2a}<br />
            <span style={{ background: `linear-gradient(135deg,${T.accent},${T.teal})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              {t.aboutH2b}
            </span>
          </h2>
          <p style={{ color: T.muted, lineHeight: 1.8, marginBottom: 14 }}>{t.aboutP1}</p>
          <p style={{ color: T.muted, lineHeight: 1.8 }}>{t.aboutP2}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {t.aboutGrid.map(([title, sub]) => (
            <div key={title} style={{ padding: "24px 20px", background: T.bg, borderRadius: 10, border: `1px solid ${T.border}` }}>
              <div className="hf" style={{ fontWeight: 700, fontSize: 15, color: T.white, marginBottom: 5 }}>{title}</div>
              <div style={{ fontSize: 12, color: T.muted }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// TESTIMONIAL CARD + REFERENCES
// ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    name: "Murat Bayram",
    role: "Geschäftsführer",
    company: "Reinigungsunternehmen (elboptimal.de)",
    avatar: "https://ui-avatars.com/api/?name=Murat+Bayram&background=7c3aed&color=fff&size=80&bold=true",
    text: "Die Zusammenarbeit für elboptimal.de war hervorragend. Unsere komplexen Dienstleistungen wie die Sonderreinigung wurden perfekt strukturiert dargestellt. Technisch läuft die React-App einwandfrei und das Design ist extrem professionell geworden.",
    stars: 5,
  },
  {
    name: "Anna Schmidt",
    role: "Head of Operations",
    company: "LogiFlow GmbH",
    avatar: "https://ui-avatars.com/api/?name=Anna+Schmidt&background=06b6d4&color=fff&size=80&bold=true",
    text: "ScaleFlowAI hat unsere internen Prozesse mit n8n-Automatisierungen komplett transformiert. Was früher Tage dauerte, erledigt das System jetzt in Minuten. Absolut empfehlenswert.",
    stars: 5,
  },
  {
    name: "Felix Berger",
    role: "CTO",
    company: "NexLayer AG",
    avatar: "https://ui-avatars.com/api/?name=Felix+Berger&background=1f2a3c&color=a78bfa&size=80&bold=true",
    text: "Vom ersten Discovery-Call bis zum Launch war die Kommunikation immer klar und professionell. Das Team liefert pünktlich und hält, was es verspricht. Die React-App performen tadellos.",
    stars: 5,
  },
];

const TestimonialCard = ({ name, role, company, avatar, text, stars }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative", padding: "32px 28px", borderRadius: 16,
        background: hovered ? `${T.accent}08` : T.surface,
        border: `1px solid ${hovered ? T.accent + "55" : T.border}`,
        boxShadow: hovered ? `0 8px 40px ${T.accentGlow}, 0 2px 12px #00000055` : "0 2px 12px #00000033",
        transition: "all .3s ease",
        display: "flex", flexDirection: "column", gap: 20,
      }}>
      <span aria-hidden="true" style={{
        position: "absolute", top: 20, right: 24, fontSize: 64, lineHeight: 1, color: T.accent,
        opacity: hovered ? 0.18 : 0.08, fontFamily: "Georgia, serif", transition: "opacity .3s",
        userSelect: "none", pointerEvents: "none",
      }}>"</span>
      <div role="img" aria-label={`${stars} von 5 Sternen`} style={{ display: "flex", gap: 3 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill={i < stars ? "#f59e0b" : T.border}>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
      <blockquote style={{ margin: 0, fontSize: 14, lineHeight: 1.8, color: T.text, fontStyle: "italic", flex: 1 }}>
        "{text}"
      </blockquote>
      <div style={{ height: 1, background: `linear-gradient(90deg, ${T.accent}33, transparent)` }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <img src={avatar} alt={`Portrait von ${name}`} width={44} height={44} loading="lazy"
          style={{ borderRadius: "50%", border: `2px solid ${hovered ? T.accent : T.border}`, transition: "border-color .3s", flexShrink: 0 }}
          onError={e => { e.target.style.display = "none"; }} />
        <div>
          <div className="hf" style={{ fontWeight: 700, fontSize: 14, color: T.white }}>{name}</div>
          <div style={{ fontSize: 11, color: T.accentLt, marginTop: 1 }}>{role}</div>
          <div style={{ fontSize: 11, color: T.muted }}>{company}</div>
        </div>
      </div>
    </article>
  );
};

const References = ({ lang }) => {
  const t = LANG[lang];
  return (
    <section id="sec-references" style={{ padding: "120px clamp(20px,5vw,64px)", background: T.bg, position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{
        position: "absolute", bottom: "-10%", right: "-5%",
        width: "40vw", maxWidth: 500, aspectRatio: "1",
        background: `radial-gradient(circle, ${T.teal}10 0%, transparent 65%)`,
        borderRadius: "50%", pointerEvents: "none",
      }} />
      <SLabel>{t.refLabel}</SLabel>
      <h2 className="hf" style={{ fontSize: "clamp(30px,5vw,52px)", fontWeight: 800, color: T.white, marginBottom: 14, lineHeight: 1.1 }}>
        {t.refH2a}{" "}
        <span style={{ background: `linear-gradient(135deg,${T.accent},${T.teal})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {t.refH2b}
        </span>
      </h2>
      <p style={{ color: T.muted, maxWidth: 500, marginBottom: 60, lineHeight: 1.75 }}>{t.refSub}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 20, alignItems: "stretch" }}>
        {TESTIMONIALS.map((item) => <TestimonialCard key={item.name} {...item} />)}
      </div>
      <div style={{ marginTop: 56, textAlign: "center" }}>
        <p style={{ fontSize: 14, color: T.muted, marginBottom: 20 }}>
          {lang === "de" ? "Werden Sie unser nächster Erfolg." : "Become our next success story."}
        </p>
        <Btn onClick={() => scrollTo(CONTACT_IDX)}>
          {lang === "de" ? "Projekt starten →" : "Start a project →"}
        </Btn>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// FAQ SECTION (NEW — accordion style)
// ─────────────────────────────────────────────────────────────
const FAQItem = ({ q, a, isOpen, onToggle, idx }) => {
  return (
    <div style={{
      borderBottom: `1px solid ${T.border}`,
      transition: "background .2s",
    }}>
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${idx}`}
        id={`faq-question-${idx}`}
        style={{
          width: "100%", padding: "22px 4px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 16, background: "none", border: "none",
          textAlign: "left", cursor: "pointer",
          color: isOpen ? T.white : T.text,
          transition: "color .2s",
        }}
        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.color = T.accentLt; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.color = T.text; }}
      >
        <span className="hf" style={{
          fontSize: "clamp(15px, 1.6vw, 17px)",
          fontWeight: 600,
          lineHeight: 1.4,
          flex: 1,
        }}>
          {q}
        </span>
        <span style={{
          flexShrink: 0,
          width: 32, height: 32, borderRadius: "50%",
          background: isOpen ? `linear-gradient(135deg,${T.accent},${T.teal})` : "transparent",
          border: isOpen ? "none" : `1px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "all .3s ease",
        }}>
          <ChevronDown size={16} color={isOpen ? "#fff" : T.muted} strokeWidth={2.5} />
        </span>
      </button>
      <div
        id={`faq-answer-${idx}`}
        role="region"
        aria-labelledby={`faq-question-${idx}`}
        className="faq-answer"
        style={{
          maxHeight: isOpen ? 400 : 0,
          opacity: isOpen ? 1 : 0,
          marginTop: isOpen ? 0 : 0,
          paddingBottom: isOpen ? 22 : 0,
        }}
      >
        <p style={{
          fontSize: 14,
          color: T.muted,
          lineHeight: 1.8,
          paddingRight: 48,
        }}>
          {a}
        </p>
      </div>
    </div>
  );
};

const FAQ = ({ lang }) => {
  const t = LANG[lang];
  const items = FAQ_ITEMS[lang];
  const [openIdx, setOpenIdx] = useState(0); // first item open by default

  return (
    <section id="sec-faq" style={{ padding: "120px clamp(20px,5vw,64px)", background: T.surface, position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{
        position: "absolute", top: "10%", right: "-8%",
        width: "35vw", maxWidth: 450, aspectRatio: "1",
        background: `radial-gradient(circle, ${T.accentGlow} 0%, transparent 65%)`,
        borderRadius: "50%", pointerEvents: "none",
      }} />

      <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 64, alignItems: "flex-start" }}>
        {/* Left column: title + CTA */}
        <div style={{ position: "sticky", top: 100 }}>
          <SLabel>{t.faqLabel}</SLabel>
          <h2 className="hf" style={{ fontSize: "clamp(30px,4.5vw,48px)", fontWeight: 800, color: T.white, marginBottom: 14, lineHeight: 1.1 }}>
            {t.faqH2a}<br />
            <span style={{ background: `linear-gradient(135deg,${T.accent},${T.teal})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              {t.faqH2b}
            </span>
          </h2>
          <p style={{ color: T.muted, lineHeight: 1.75, marginBottom: 28, maxWidth: 380 }}>{t.faqSub}</p>

          <div style={{
            padding: "20px 24px", background: T.bg, borderRadius: 12,
            border: `1px solid ${T.border}`, maxWidth: 380,
          }}>
            <p style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 6 }}>{t.faqStillQuestions}</p>
            <button
              onClick={() => scrollTo(CONTACT_IDX)}
              style={{
                background: "none", border: "none", padding: 0,
                color: T.accentLt, fontSize: 13, fontWeight: 700,
                fontFamily: "'Bricolage Grotesque',sans-serif",
                cursor: "pointer", textDecoration: "underline",
              }}>
              {t.faqContactUs}
            </button>
          </div>
        </div>

        {/* Right column: accordion */}
        <div style={{ maxWidth: 720 }}>
          <div style={{ borderTop: `1px solid ${T.border}` }}>
            {items.map((item, i) => (
              <FAQItem
                key={i}
                idx={i}
                q={item.q}
                a={item.a}
                isOpen={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// CONTACT FORM  (updated with GDPR consent checkbox)
// ─────────────────────────────────────────────────────────────
const ContactForm = ({ lang, onOpenLegal }) => {
  const t = LANG[lang];
  const HP = "company_website";
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "", [HP]: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);

  const change = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    const err = validateField(name, value);
    setErrors(p => ({ ...p, [name]: err ? err[lang] : null }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form[HP]) return;

    if (!consent) { setConsentError(true); return; }
    setConsentError(false);

    if (!rateLimiter.check()) { setStatus("rate"); return; }

    const newErrors = {};
    ["name", "email", "message"].forEach(f => {
      if (!form[f].trim()) newErrors[f] = t.fErrRequired;
      else { const err = validateField(f, form[f]); if (err) newErrors[f] = err[lang]; }
    });
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setStatus("sending");

    try {
      const res = await fetch("https://formspree.io/f/xgornjer", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          name: sanitize(form.name),
          email: sanitize(form.email),
          phone: sanitize(form.phone),
          service: sanitize(form.service),
          message: sanitize(form.message),
          consent_given: consent ? "yes" : "no",
          consent_timestamp: new Date().toISOString(),
          _replyto: sanitize(form.email),
          _subject: `ScaleFlowAI – Neue Anfrage von ${sanitize(form.name)}`,
        }),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  const inpStyle = (f) => ({
    width: "100%", background: T.bg, border: `1px solid ${errors[f] ? "#f87171" : T.border}`,
    borderRadius: 8, padding: "11px 15px", color: T.text, fontSize: 13,
    outline: "none", transition: "border-color .2s",
  });
  const lblStyle = {
    fontSize: 11, fontFamily: "'Bricolage Grotesque',sans-serif",
    letterSpacing: "0.12em", textTransform: "uppercase", color: T.muted, display: "block", marginBottom: 7
  };

  return (
    <section id="sec-contact" style={{ padding: "120px clamp(20px,5vw,64px)" }}>
      <div style={{ maxWidth: 680 }}>
        <SLabel>{t.ctaLabel}</SLabel>
        <h2 className="hf" style={{ fontSize: "clamp(30px,5vw,48px)", fontWeight: 800, color: T.white, marginBottom: 14, lineHeight: 1.1 }}>
          {t.ctaH2a}<br />
          <span style={{ background: `linear-gradient(135deg,${T.accent},${T.teal})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {t.ctaH2b}
          </span>
        </h2>
        <p style={{ color: T.muted, marginBottom: 48, lineHeight: 1.75 }}>{t.ctaSub}</p>

        {status === "success" ? (
          <div style={{ padding: "40px 32px", background: `${T.accent}12`, border: `1px solid ${T.accent}44`, borderRadius: 12, textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 14 }}>✓</div>
            <h3 className="hf" style={{ fontSize: 22, fontWeight: 700, color: T.white, marginBottom: 8 }}>{t.fSuccess}</h3>
            <p style={{ color: T.muted }}>{t.fSuccessSub}</p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <input
              type="text" name={HP} value={form[HP]} onChange={change}
              style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
              tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 18, marginBottom: 18 }}>
              <div>
                <label htmlFor="sf-name" style={lblStyle}>{t.fName} <span style={{ color: "#f87171" }} aria-label="Pflichtfeld">*</span></label>
                <input id="sf-name" name="name" value={form.name} onChange={change} placeholder={t.fNamePh}
                  style={inpStyle("name")} autoComplete="name"
                  onFocus={e => e.target.style.borderColor = T.accent}
                  onBlur={e => e.target.style.borderColor = errors.name ? "#f87171" : T.border} />
                {errors.name && <p role="alert" style={{ color: "#f87171", fontSize: 11, marginTop: 4 }}>{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="sf-email" style={lblStyle}>{t.fEmail} <span style={{ color: "#f87171" }} aria-label="Pflichtfeld">*</span></label>
                <input id="sf-email" name="email" type="email" value={form.email} onChange={change} placeholder={t.fEmailPh}
                  style={inpStyle("email")} autoComplete="email"
                  onFocus={e => e.target.style.borderColor = T.accent}
                  onBlur={e => e.target.style.borderColor = errors.email ? "#f87171" : T.border} />
                {errors.email && <p role="alert" style={{ color: "#f87171", fontSize: 11, marginTop: 4 }}>{errors.email}</p>}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 18, marginBottom: 18 }}>
              <div>
                <label htmlFor="sf-phone" style={lblStyle}>{t.fPhone}</label>
                <input id="sf-phone" name="phone" value={form.phone} onChange={change} placeholder={t.fPhonePh}
                  style={inpStyle("phone")} autoComplete="tel"
                  onFocus={e => e.target.style.borderColor = T.accent}
                  onBlur={e => e.target.style.borderColor = errors.phone ? "#f87171" : T.border} />
                {errors.phone && <p role="alert" style={{ color: "#f87171", fontSize: 11, marginTop: 4 }}>{errors.phone}</p>}
              </div>
              <div>
                <label htmlFor="sf-service" style={lblStyle}>{t.fService}</label>
                <select id="sf-service" name="service" value={form.service} onChange={change}
                  style={{ ...inpStyle("service"), appearance: "none" }}>
                  {t.fServiceOpts.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label htmlFor="sf-msg" style={lblStyle}>{t.fMsg} <span style={{ color: "#f87171" }} aria-label="Pflichtfeld">*</span></label>
              <textarea id="sf-msg" name="message" rows={5} value={form.message} onChange={change} placeholder={t.fMsgPh}
                style={{ ...inpStyle("message"), resize: "vertical" }}
                onFocus={e => e.target.style.borderColor = T.accent}
                onBlur={e => e.target.style.borderColor = errors.message ? "#f87171" : T.border} />
              {errors.message && <p role="alert" style={{ color: "#f87171", fontSize: 11, marginTop: 4 }}>{errors.message}</p>}
            </div>

            {/* GDPR CONSENT CHECKBOX (new) */}
            <div style={{
              marginBottom: 24,
              padding: "16px 18px",
              background: consentError ? `${T.accent}0a` : T.surface,
              border: `1px solid ${consentError ? "#f87171" : T.border}`,
              borderRadius: 10,
              transition: "all .2s",
            }}>
              <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => { setConsent(e.target.checked); if (e.target.checked) setConsentError(false); }}
                  style={{
                    marginTop: 3,
                    width: 18, height: 18,
                    accentColor: T.accent,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  aria-required="true"
                  aria-invalid={consentError}
                />
                <span style={{ fontSize: 12, color: T.text, lineHeight: 1.6 }}>
                  {t.fConsentText}{" "}
                  <button
                    type="button"
                    onClick={() => onOpenLegal(0)}
                    style={{
                      background: "none", border: "none", padding: 0,
                      color: T.accentLt, textDecoration: "underline",
                      cursor: "pointer", fontSize: 12, fontFamily: "inherit",
                    }}>
                    {t.fConsentLink}
                  </button>
                  {" "}<span style={{ color: "#f87171" }} aria-label="Pflichtfeld">*</span>
                </span>
              </label>
              {consentError && (
                <p role="alert" style={{ color: "#f87171", fontSize: 11, marginTop: 8, marginLeft: 30 }}>
                  {t.fConsentRequired}
                </p>
              )}
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
              <Btn
                type="submit"
                disabled={!consent || status === "sending"}
              >
                {status === "sending" ? t.fSending : t.fSend}
              </Btn>
              <Btn as="a" variant="outline"
                href={waLink(lang === "de" ? "Hallo ScaleFlowAI! Ich möchte ein Projekt besprechen." : "Hello ScaleFlowAI! I'd like to discuss a project.")}>
                <span style={{ fontSize: 17 }}>💬</span> {t.fWa}
              </Btn>
            </div>

            {status === "error" && (
              <p role="alert" style={{ color: "#f87171", fontSize: 12, marginTop: 14 }}>
                {lang === "de" ? "Fehler beim Senden. Bitte versuchen Sie es erneut." : "Error sending. Please try again."}
              </p>
            )}
            {status === "rate" && (
              <p role="alert" style={{ color: "#f87171", fontSize: 12, marginTop: 14 }}>{t.fErrRate}</p>
            )}
            <p style={{ fontSize: 11, color: T.muted, marginTop: 16 }}>{t.fPrivacy}</p>
          </form>
        )}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// FOOTER  (updated with social media icons)
// ─────────────────────────────────────────────────────────────
const Footer = ({ lang, onLegal }) => {
  const t = LANG[lang];
  return (
    <footer style={{ background: T.surface, borderTop: `1px solid ${T.border}`, padding: "56px clamp(20px,5vw,64px) 32px" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
        gap: 40, marginBottom: 36,
      }}>
        {/* Brand */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Logo size={26} />
            <span className="hf" style={{ fontWeight: 800, color: T.white, fontSize: 16 }}>
              Scale<span style={{ color: T.accentLt }}>Flow</span><span style={{ color: T.teal }}>AI</span>
            </span>
          </div>
          <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.7, maxWidth: 280 }}>
            {t.footerTagline}
          </p>
        </div>

        {/* Navigation */}
        <div>
          <p style={{
            fontSize: 10, color: T.muted, letterSpacing: "0.16em",
            textTransform: "uppercase", fontFamily: "'Bricolage Grotesque',sans-serif",
            fontWeight: 700, marginBottom: 14,
          }}>
            Navigation
          </p>
          <nav aria-label="Footer Navigation" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {t.nav.map((l, i) => (
              <button key={i} onClick={() => scrollTo(i)} style={{
                background: "none", border: "none", fontSize: 12, color: T.muted,
                cursor: "pointer", transition: "color .2s", textAlign: "left", padding: 0,
              }}
                onMouseEnter={e => e.target.style.color = T.accentLt}
                onMouseLeave={e => e.target.style.color = T.muted}>{l}</button>
            ))}
          </nav>
        </div>

        {/* Social media */}
        <div>
          <p style={{
            fontSize: 10, color: T.muted, letterSpacing: "0.16em",
            textTransform: "uppercase", fontFamily: "'Bricolage Grotesque',sans-serif",
            fontWeight: 700, marginBottom: 14,
          }}>
            {t.footerFollow}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {SOCIAL_LINKS.map(({ name, url, icon }) => (
              <a
                key={name}
                href={url}
                target={url.startsWith("http") ? "_blank" : undefined}
                rel={url.startsWith("http") ? "noopener noreferrer" : undefined}
                aria-label={name}
                title={name}
                style={{
                  width: 38, height: 38, borderRadius: 10,
                  border: `1px solid ${T.border}`,
                  background: T.bg,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: "#ffffff", // Hier Standardmäßig auf Weiß setzen
                  transition: "all .25s ease",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = T.accent;
                  e.currentTarget.style.color = T.accentLt; // Das Icon wird beim Hover heller/farbig
                  e.currentTarget.style.background = `${T.accent}12`;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = T.border;
                  e.currentTarget.style.color = "#ffffff"; // Zurück zu Weiß
                  e.currentTarget.style.background = T.bg;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <FontAwesomeIcon icon={icon} style={{ fontSize: "18px", color: "#ffffff"}} />
              </a>
            ))}
          </div>
          <p style={{ fontSize: 11, color: T.muted, marginTop: 16 }}>
            info@scaleflowai.com
          </p>
        </div>
      </div>

      <div style={{
        paddingTop: 24, borderTop: `1px solid ${T.border}`,
        display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <p style={{ fontSize: 11, color: T.muted }}>
          © {new Date().getFullYear()} ScaleFlowAI. {t.footerRights}
        </p>
        <div style={{ display: "flex", gap: 20 }}>
          {t.footerLinks.map((l, i) => (
            <button key={l} onClick={() => onLegal(i)} style={{
              fontSize: 11, color: T.muted, background: "none", border: "none",
              cursor: "pointer", transition: "color .2s", padding: 0,
            }}
              onMouseEnter={e => e.target.style.color = T.accentLt}
              onMouseLeave={e => e.target.style.color = T.muted}>{l}</button>
          ))}
        </div>
      </div>
    </footer>
  );
};

// ─────────────────────────────────────────────────────────────
// CHATBOT
// ─────────────────────────────────────────────────────────────
const ChatBot = ({ lang }) => {
  const t = LANG[lang];
  const flows = BOT_FLOWS[lang];
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ from: "bot", text: t.botWelcome, options: t.botOptions }]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const goKey = lang === "de" ? "Zum Kontaktformular" : "Go to Contact Form";

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, typing]);
  useEffect(() => {
    setMsgs([{ from: "bot", text: t.botWelcome, options: t.botOptions }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const botReply = (text, options) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMsgs(p => [...p, { from: "bot", text, options }]);
    }, 700);
  };

  const handleOption = (opt) => {
    setMsgs(p => [...p, { from: "user", text: opt }]);
    if (opt === goKey) { scrollTo(CONTACT_IDX); setOpen(false); return; }
    const flow = flows[opt] || {
      text: lang === "de" ? "Lassen Sie mich Sie mit unserem Team verbinden!" : "Let me connect you with our team!",
      options: [goKey]
    };
    botReply(flow.text, flow.options);
  };

  const handleSend = () => {
    const val = input.trim();
    if (!val) return;
    if (isSqlInjection(val) || isXss(val)) return;
    setInput("");
    setMsgs(p => [...p, { from: "user", text: val }]);
    const lower = val.toLowerCase();
    let matchedKey = null;
    if (lang === "de") {
      if (lower.includes("web")) matchedKey = "Web-Entwicklung";
      else if (lower.includes("ki") || lower.includes("ai") || lower.includes("auto")) matchedKey = "KI-Automatisierung";
      else if (lower.includes("preis") || lower.includes("kosten")) matchedKey = "Preise";
      else if (lower.includes("prozess")) matchedKey = "Prozessautomatisierung";
      else if (lower.includes("kontakt") || lower.includes("anruf")) matchedKey = "Kontakt aufnehmen";
    } else {
      if (lower.includes("web")) matchedKey = "Web Development";
      else if (lower.includes("ai") || lower.includes("auto")) matchedKey = "AI Automation";
      else if (lower.includes("pric") || lower.includes("cost")) matchedKey = "Pricing";
      else if (lower.includes("process")) matchedKey = "Process Automation";
      else if (lower.includes("contact") || lower.includes("call")) matchedKey = "Contact us";
    }
    const flow = matchedKey ? flows[matchedKey] : {
      text: lang === "de"
        ? "Danke für Ihre Nachricht! Am besten füllen Sie unser Kontaktformular aus, dann melden wir uns bei Ihnen."
        : "Thanks for your message! Your best next step is filling in our contact form.",
      options: [goKey]
    };
    botReply(flow.text, flow.options);
  };

  return (
    <>
      <button
        onClick={() => setOpen(p => !p)}
        aria-label={open ? (lang === "de" ? "Chat schließen" : "Close chat") : (lang === "de" ? "Chat öffnen" : "Open chat")}
        aria-expanded={open}
        style={{
          position: "fixed", bottom: 28, right: 28, width: 54, height: 54, borderRadius: "50%",
          background: `linear-gradient(135deg,${T.accent},${T.teal})`, color: "#fff",
          border: "none", cursor: "pointer", zIndex: 999,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, boxShadow: `0 4px 24px ${T.accentGlow}`,
          transition: "transform .2s ease",
          transform: open ? "scale(0.92)" : "scale(1)",
        }}>
        {open ? "✕" : "💬"}
        {!open && (
          <span aria-hidden="true" style={{
            position: "absolute", top: -2, right: -2, width: 13, height: 13, background: "#f87171",
            borderRadius: "50%", border: `2px solid ${T.bg}`, animation: "pulseRing 2s infinite",
          }} />
        )}
      </button>

      <div
        role="dialog" aria-modal="true"
        aria-label={lang === "de" ? "ScaleFlowAI Chat-Assistent" : "ScaleFlowAI Chat Assistant"}
        style={{
          position: "fixed", bottom: 94, right: 28, width: "min(360px,calc(100vw-40px))",
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16,
          boxShadow: "0 24px 72px #00000099", zIndex: 998,
          transform: open ? "scale(1) translateY(0)" : "scale(.93) translateY(18px)",
          opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none",
          transition: "all .25s cubic-bezier(.4,0,.2,1)",
          display: "flex", flexDirection: "column", maxHeight: "72vh",
        }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: `linear-gradient(135deg,${T.accent},${T.teal})`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17
          }} aria-hidden="true">🤖</div>
          <div>
            <div className="hf" style={{ fontWeight: 700, fontSize: 13, color: T.white }}>ScaleFlowAI</div>
            <div style={{ fontSize: 11, color: T.teal, display: "flex", alignItems: "center", gap: 4 }}>
              <span aria-hidden="true" style={{ width: 6, height: 6, background: T.teal, borderRadius: "50%", display: "inline-block" }} />
              {t.botOnline}
            </div>
          </div>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
            style={{ marginLeft: "auto", fontSize: 20, color: "#25D366" }}>📱</a>
        </div>

        <div style={{ overflowY: "auto", padding: "14px 14px 6px", flex: 1, display: "flex", flexDirection: "column", gap: 10 }}
          aria-live="polite" aria-atomic="false">
          {msgs.map((m, i) => {
            const isLastWithOptions = m.options && i === msgs.reduce((last, msg, idx) => msg.options ? idx : last, -1);
            return (
              <div key={i}>
                <div style={{
                  maxWidth: "82%", padding: "9px 13px", borderRadius: 10, fontSize: 13, lineHeight: 1.6,
                  ...(m.from === "bot"
                    ? { background: T.bg, color: T.text, borderBottomLeftRadius: 2 }
                    : { background: `linear-gradient(135deg,${T.accent},${T.teal}cc)`, color: "#fff", marginLeft: "auto", borderBottomRightRadius: 2 }),
                }}>{m.text}</div>
                {isLastWithOptions && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
                    {m.options.map(opt => (
                      <button key={opt} onClick={() => handleOption(opt)} style={{
                        padding: "5px 10px", borderRadius: 6, border: `1px solid ${T.accent}55`,
                        background: "transparent", color: T.accentLt, fontSize: 11,
                        fontFamily: "'Bricolage Grotesque',sans-serif", cursor: "pointer", transition: "all .2s",
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = T.accent; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.accentLt; }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {typing && (
            <div aria-label={lang === "de" ? "Schreibt..." : "Typing..."} style={{ background: T.bg, borderRadius: 10, padding: "10px 14px", width: 60, display: "flex", gap: 4, alignItems: "center" }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{ width: 6, height: 6, background: T.muted, borderRadius: "50%", animation: `pulseRing .9s ease-in-out ${i * .2}s infinite` }} />
              ))}
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div style={{ padding: "10px 12px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
          <input
            ref={inputRef} value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSend()}
            placeholder={t.botInputPh} maxLength={300} aria-label={t.botInputPh}
            style={{
              flex: 1, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
              padding: "8px 12px", color: T.text, fontSize: 12, outline: "none",
            }}
            onFocus={e => e.target.style.borderColor = T.accent}
            onBlur={e => e.target.style.borderColor = T.border}
          />
          <button onClick={handleSend} aria-label={t.botSend} style={{
            padding: "8px 14px", borderRadius: 8,
            background: `linear-gradient(135deg,${T.accent},${T.teal})`,
            color: "#fff", fontSize: 12, fontFamily: "'Bricolage Grotesque',sans-serif",
            fontWeight: 700, border: "none", cursor: "pointer",
          }}>{t.botSend}</button>
        </div>

        <div style={{ padding: "6px 14px 10px", fontSize: 10, color: T.muted, textAlign: "center" }}>
          {t.botWaHint}{" "}
          <a href={waLink()} target="_blank" rel="noopener noreferrer" style={{ color: "#25D366" }}>WhatsApp</a>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// COOKIE BANNER
// ─────────────────────────────────────────────────────────────
const CookieBanner = ({ lang }) => {
  const t = LANG[lang];
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoices] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      if (!localStorage.getItem("sfa_cookies")) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (v) => {
    try { localStorage.setItem("sfa_cookies", JSON.stringify(v)); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label={t.cookieTitle} style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 2000,
      background: T.surface, borderTop: `1px solid ${T.border}`,
      boxShadow: "0 -8px 40px #00000088", animation: "fadeUp .4s ease both",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px clamp(16px,4vw,48px)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 20 }}>
          <div style={{ flex: "1 1 400px" }}>
            <h3 className="hf" style={{ fontWeight: 700, fontSize: 16, color: T.white, marginBottom: 8 }}>{t.cookieTitle}</h3>
            <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.7 }}>{t.cookieText}</p>
            <button onClick={() => setShowDetails(p => !p)} style={{
              marginTop: 8, background: "none", border: "none", color: T.accentLt,
              fontSize: 12, cursor: "pointer", textDecoration: "underline",
            }}>
              {showDetails ? (lang === "de" ? "Weniger anzeigen" : "Show less") : (lang === "de" ? "Details anzeigen" : "Show details")}
            </button>

            {showDetails && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { key: "necessary", label: t.cookieNec, desc: t.cookieNecDesc, locked: true },
                  { key: "analytics", label: t.cookieAna, desc: t.cookieAnaDesc, locked: false },
                  { key: "marketing", label: t.cookieMkt, desc: t.cookieMktDesc, locked: false },
                ].map(({ key, label, desc, locked }) => (
                  <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: locked ? "default" : "pointer" }}>
                    <div style={{
                      marginTop: 2, width: 36, height: 20, borderRadius: 10, position: "relative",
                      background: choices[key] ? `linear-gradient(135deg,${T.accent},${T.teal})` : T.border,
                      transition: "background .2s", flexShrink: 0,
                    }} onClick={() => { if (!locked) setChoices(p => ({ ...p, [key]: !p[key] })); }}
                      role={locked ? undefined : "switch"} aria-checked={choices[key]}
                      tabIndex={locked ? -1 : 0}
                      onKeyDown={e => { if (!locked && (e.key === "Enter" || e.key === " ")) setChoices(p => ({ ...p, [key]: !p[key] })); }}>
                      <span style={{
                        position: "absolute", top: 3, width: 14, height: 14, borderRadius: "50%",
                        background: "#fff", transition: "left .2s",
                        left: choices[key] ? 19 : 3,
                      }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>
                        {label}
                        {locked && <span style={{ color: T.muted, marginLeft: 6, fontSize: 10 }}>({lang === "de" ? "Erforderlich" : "Required"})</span>}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted }}>{desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 160 }}>
            <Btn onClick={() => save({ necessary: true, analytics: true, marketing: true })} sx={{ justifyContent: "center" }}>{t.cookieAcceptAll}</Btn>
            <Btn variant="outline" onClick={() => save(choices)} sx={{ justifyContent: "center" }}>{t.cookieAcceptSel}</Btn>
            <button onClick={() => save({ necessary: true, analytics: false, marketing: false })} style={{
              fontSize: 12, color: T.muted, background: "none", border: "none", cursor: "pointer",
              textAlign: "center", textDecoration: "underline",
            }}>{t.cookieDecline}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState("de");
  const [legalModal, setLegalModal] = useState(null);

  return (
    <>
      <SEO lang={lang} />
      <style>{GLOBAL_CSS}</style>
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} lang={lang} setLang={setLang} />
      <SlideMenu open={menuOpen} setOpen={setMenuOpen} lang={lang} setLang={setLang} />
      <main>
        <Hero lang={lang} />
        <Services lang={lang} />
        <Pricing lang={lang} />
        <Process lang={lang} />
        <About lang={lang} />
        <References lang={lang} />
        <FAQ lang={lang} />
        <ContactForm lang={lang} onOpenLegal={setLegalModal} />
      </main>
      <Footer lang={lang} onLegal={setLegalModal} />
      <ChatBot lang={lang} />
      <CookieBanner lang={lang} />
      {legalModal !== null && (
        <LegalModal lang={lang} which={legalModal} onClose={() => setLegalModal(null)} />
      )}
    </>
  );
}
